import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    TextField,
    Button
} from '@mui/material';
import ImageUploader from './ImageUploader';
import { useTranslation } from 'react-i18next';

const CreateSiteForm = ({ initialValues, onFormChange }) => {
    const { t } = useTranslation();
    const [resetTrigger, setResetTrigger] = useState(false);

    const [formValues, setFormValues] = useState({
        planId: 'basic',
        coupleName: '',
        startDate: '',
        startTime: '',
        startDateTime: '',
        message: '',
        youTubeUrl: '',
        imageUrls: [],
        ...initialValues
    });

    useEffect(() => {
        setFormValues((prevValues) => ({ ...prevValues, ...initialValues }));
    }, [initialValues]);

    const handlePlanChange = (selectedPlan) => {
        const updatedValues = { ...formValues, planId: selectedPlan, imageUrls: [] };
        setFormValues(updatedValues);
        onFormChange && onFormChange(updatedValues);    
        setResetTrigger(selectedPlan);
    };

    const handleInputChange = (field, value) => {
        let updatedValues = { ...formValues, [field]: value };

        if (field === 'startDate' || field === 'startTime') {
            const startDate = field === 'startDate' ? value : formValues.startDate;
            const startTime = field === 'startTime' ? value : formValues.startTime || '00:00';

            if (startDate) {
                const validStartTime = startTime.match(/^([01]\d|2[0-3]):([0-5]\d)$/) ? startTime : '00:00';
                updatedValues.startDateTime = `${startDate}T${validStartTime}:00`;
            } else {
                updatedValues.startDateTime = '';
            }
        }

        setFormValues(updatedValues);
        onFormChange && onFormChange(updatedValues);
    };

    const handleUploadComplete = (urls) => {
        const updatedValues = { ...formValues, imageUrls: urls };
        setFormValues(updatedValues);
        onFormChange && onFormChange(updatedValues);
    };

    return (
        <Box component="form" noValidate autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ position: 'relative', display: 'flex', border: '1px solid #5947a6', borderRadius: '4px', padding: '10px', textAlign: 'center', width: '100%', margin: '0 auto' }}>
                <Typography component="label" sx={{ position: 'absolute', top: '-10px', left: '12px', backgroundColor: 'white', padding: '0 4px', fontSize: '0.75rem', color: '#5947a6' }}>
                    {t('create_form_plan_label')}
                </Typography>
                <Button fullWidth onClick={() => handlePlanChange('basic')} sx={{ borderRadius: '8px', fontSize: '1rem', fontWeight: 'normal', lineHeight: '1.5rem', padding: '8px', backgroundColor: formValues.planId === 'basic' ? '#5947a6' : 'transparent', color: formValues.planId === 'basic' ? '#fff' : 'secondary.main', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}>
                    {t('create_form_plan_basic_label')} <br /> 6,00 €
                </Button>
                <Button fullWidth onClick={() => handlePlanChange('premium')} sx={{ borderRadius: '8px', fontSize: '1rem', fontWeight: 'normal', lineHeight: '1.5rem', padding: '8px', backgroundColor: formValues.planId === 'premium' ? '#5947a6' : 'transparent', color: formValues.planId === 'premium' ? '#fff' : 'secondary.main', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}>
                    {t('create_form_plan_exclusive_label')} <br /> 9,00 €
                </Button>
            </Box>

            <TextField
                fullWidth
                label={t('create_form_email_label')}
                placeholder={t('create_form_email_placeholder')}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={formValues.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                helperText={t('create_form_email_helper')}
            />

            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={t('create_form_couple_name_label')}
                        placeholder={t('create_form_couple_name_placeholder')}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        value={formValues.coupleName}
                        onChange={(e) => handleInputChange('coupleName', e.target.value)}
                    />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <TextField
                        fullWidth
                        label={t('create_form_start_date_label')}
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        value={formValues.startDate}
                        onChange={(e) => handleInputChange('startDate', e.target.value)}
                    />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <TextField
                        fullWidth
                        label={t('create_form_start_time_label')}
                        type="time"
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        value={formValues.startTime}
                        onChange={(e) => handleInputChange('startTime', e.target.value)}
                    />
                </Grid>
            </Grid>

            <TextField
                fullWidth
                label={t('create_form_message_label')}
                placeholder={t('create_form_message_placeholder')}
                variant="outlined"
                InputProps={{
                    rows: 8,
                    multiline: true,
                    inputComponent: 'textarea'
                }}
                InputLabelProps={{ shrink: true }}
                value={formValues.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
            />

            {formValues.planId === 'premium' && (
                <TextField
                    fullWidth
                    label={t('create_form_youtube_label')}
                    placeholder="https://www.youtube.com/watch?v=PMivT7MJ41M"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    value={formValues.youTubeUrl}
                    onChange={(e) => handleInputChange('youTubeUrl', e.target.value)}
                />
            )}

            <ImageUploader
                autoUpload
                buttonLabel={t(formValues.planId === 'premium' ? 'image_uploader_button_label_exclusive' : 'image_uploader_button_label_basic')}
                initialImageUrls={formValues.imageUrls}
                maxImages={formValues.planId === 'premium' ? 7 : 3}
                onUploadComplete={handleUploadComplete}
                resetTrigger={resetTrigger}
            />
        </Box>
    );
};

export default CreateSiteForm;
